
import dayjs from "dayjs";
import {
  IFreshCreateOrUpdateCatData,
} from "libs/api/cats/src/lib/types/abandoned-cart/cat-create-or-update.types";
import {
  IFreshCreateOrUpdateCatsRequest,
  IFreshCustomerSUFAnswers,
} from "@api-clients/cats";
import {
  IGetPriceEstimateRequest,
  IGetPriceEstimateResponse,
  ICatRecipeDetails,
} from "@api-clients/checkout";
import {
  calculateSuggestedCaloriesFromBirthday,
  calculateTraySize,
  getAgeAtSignup,
} from "@./cat-calculations";

import { CatLifeStage, PricingPlan } from "@/primary-models";

import {
  CatFieldName,
  ICatFormData,
  IDiscount,
  IRecipeItem,
  ISubscriptionData,
  IUserFormData,
  THealthConcernsToAddress,
  TMotivationsForSwitch,
} from "./shared/types";
import { defaultDiscountByTrialPlan } from "./constants";

const LEGACY_TO_PRICING_PLAN: Record<string, PricingPlan> = {
  PlanTrial14x14: PricingPlan.TRIAL,
  Plan28x28: PricingPlan.STANDARD,
  Plan28x56: PricingPlan.HALF,
  Plan14x14: PricingPlan.FREEZER,
  Plan42x42: PricingPlan.SAVER,
  Plan7x7: PricingPlan.TRIAL_WEEK,
};

const VALID_PRICING_PLANS = new Set<string>(Object.values(PricingPlan));

/**
 * Normalises a plan type that may be a legacy Kotlin MealPlans enum name
 * (e.g. "PlanTrial14x14") to the current PricingPlan enum value (e.g. "TRIAL").
 * Returns the fallback when the value is missing or unrecognised.
 */
export function normalisePlanType(
  value: string | undefined | null,
  fallback: PricingPlan,
): PricingPlan {
  if (!value) {
    return fallback;
  }
  if (VALID_PRICING_PLANS.has(value)) {
    return value as PricingPlan;
  }
  return LEGACY_TO_PRICING_PLAN[value] ?? fallback;
}

/**
 * Returns a non-empty discount code from either a legacy string discount shape
 * or the current object shape. Empty strings are treated as missing.
 */
export function getDiscountCode(discount: unknown): string | undefined {
  if (typeof discount === "string") {
    const legacyCode = discount.trim();
    return legacyCode || undefined;
  }

  if (discount && typeof discount === "object" && !Array.isArray(discount)) {
    const code = (discount as Record<string, unknown>)["discountCode"];
    if (typeof code === "string") {
      const normalisedCode = code.trim();
      return normalisedCode || undefined;
    }
  }

  return undefined;
}

function resolveFallbackMetadata(
  fallback: IDiscount | undefined,
  discountCode: string,
): Pick<IDiscount, "type" | "value"> {
  if (fallback?.discountCode !== discountCode) {
    return { type: undefined, value: undefined };
  }
  return { type: fallback.type, value: fallback.value };
}

/**
 * Normalises persisted discount data across legacy and current store shapes.
 * - Legacy string discount values are mapped to discountCode.
 * - Empty or missing discountCode falls back to the supplied discount, when available.
 */
export function normaliseDiscount(
  discount: unknown,
  fallback?: IDiscount,
): IDiscount | undefined {
  const discountCode = getDiscountCode(discount);

  if (!discountCode) {
    return fallback ? { ...fallback } : undefined;
  }

  const { type: fallbackType, value: fallbackValue } = resolveFallbackMetadata(fallback, discountCode);

  if (discount && typeof discount === "object" && !Array.isArray(discount)) {
    const discountObj = discount as Record<string, unknown>;
    return {
      discountCode,
      type: (discountObj["type"] as IDiscount["type"]) ?? fallbackType,
      value: typeof discountObj["value"] === "string" ? discountObj["value"] : fallbackValue,
    };
  }

  return { discountCode, type: fallbackType, value: fallbackValue };
}

export const getDefaultDiscountForTrialPlan = (planType?: PricingPlan): IDiscount | undefined => {
  if (!planType) {
    return undefined;
  }

  const defaultDiscountForPlan = defaultDiscountByTrialPlan[planType];
  return defaultDiscountForPlan ? { ...defaultDiscountForPlan } : undefined;
};

export const resolveEffectiveDiscountCode = (
  requestedDiscountCode: IGetPriceEstimateRequest["discountCode"],
  subscription: ISubscriptionData,
): string | undefined => {
  const requestCode = getDiscountCode(requestedDiscountCode);
  const storeCode = getDiscountCode(subscription.discount);
  const defaultDiscountForPlan = getDefaultDiscountForTrialPlan(subscription.trial?.planType);

  return requestCode ?? storeCode ?? defaultDiscountForPlan?.discountCode;
};

export const normaliseApiDiscountToStoreDiscount = (
  discount: IGetPriceEstimateResponse["discount"] | undefined,
  subscription: ISubscriptionData,
): ISubscriptionData["discount"] | undefined => normaliseDiscount(
  discount?.code
    ? {
      discountCode: discount.code,
      type: discount.discountType,
      value: discount.value,
    }
    : subscription.discount,
  getDefaultDiscountForTrialPlan(subscription.trial?.planType),
);

export const hasDiscountChanged = (
  currentDiscount: unknown,
  nextDiscount: ISubscriptionData["discount"] | undefined,
): boolean => {
  if (!nextDiscount) {
    return false;
  }

  // Persisted stores can still rehydrate a legacy string shape at runtime.
  if (typeof currentDiscount === "string") {
    return true;
  }

  const currentDiscountMetadata = currentDiscount && typeof currentDiscount === "object" && !Array.isArray(currentDiscount)
    ? currentDiscount as Partial<IDiscount>
    : undefined;

  return (
    getDiscountCode(currentDiscount) !== getDiscountCode(nextDiscount)
    || currentDiscountMetadata?.type !== nextDiscount.type
    || currentDiscountMetadata?.value !== nextDiscount.value
  );
};

export const getRehydratedSubscriptionUpdate = (
  subscription: ISubscriptionData,
): Partial<ISubscriptionData> | undefined => {
  const trialPlan = normalisePlanType(subscription.trial?.planType, PricingPlan.TRIAL);
  const ongoingPlan = normalisePlanType(subscription.ongoing?.planType, PricingPlan.STANDARD);
  const planTypeChanged =
    trialPlan !== subscription.trial?.planType ||
    ongoingPlan !== subscription.ongoing?.planType;

  const normalisedDiscount = normaliseDiscount(
    subscription.discount,
    getDefaultDiscountForTrialPlan(trialPlan),
  );
  const discountChanged = hasDiscountChanged(subscription.discount, normalisedDiscount);

  if (!planTypeChanged && !discountChanged) {
    return undefined;
  }

  return {
    ...(planTypeChanged && {
      trial: { ...subscription.trial, planType: trialPlan },
      ongoing: { ...subscription.ongoing, planType: ongoingPlan },
    }),
    ...(discountChanged && normalisedDiscount && { discount: normalisedDiscount }),
  };
};
const DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_CAT_WEIGHT_GRAMS = 4000;
const DEFAULT_CAT_BIRTHDAY = dayjs().format(DATE_FORMAT);

/**
 * Calculates the standardised daily calories for a cat based on their data.
 * Returns the standardised calorie value from the tray size mapping.
 */
export function calculateCaloriesForCat(cat: ICatFormData): number | undefined {
  if (!cat.weight?.valueInKg || !cat.body?.bodyType || !cat.body?.activityLevel || !cat.birthday) {
    return undefined;
  }

  const suggestedCalories = calculateSuggestedCaloriesFromBirthday(
    cat.weight.valueInKg,
    cat.body.bodyType,
    cat.body.activityLevel,
    dayjs(cat.birthday),
  );

  const mealPlan = calculateTraySize(suggestedCalories);
  return mealPlan.calories;
}

/**
 * Safely retrieves the value of a nested field from an object using dot notation.
 *
 * @param obj - The source object to extract the value from (`ICatFormData`).
 * @param path - A dot-separated string (`CatFieldName`) representing the path to the nested field
 *               (e.g. `"name"` or `"age.years"`).
 * @returns The value at the specified path, or `undefined` if the path is invalid.
 */
export const getNestedFieldValue = (obj: ICatFormData, path: CatFieldName): unknown => path.split(".").reduce((acc: unknown, key) => (acc && typeof acc === "object" && acc !== null
  ? (acc as Record<string, unknown>)[key]
  : undefined), obj);

/**
 * Returns a new object with the specified nested field removed.
 *
 * @param obj - The original object (`ICatFormData`) to remove the field from.
 * @param path - A dot-separated string (`CatFieldName`)representing the path to the field to remove
 *               (e.g. `"age.years"` or `"name"`).
 * @returns A new `ICatFormData` object with the field removed.
 */
export const removeNestedField = (obj: ICatFormData, path: CatFieldName): ICatFormData => {
  const keys = path.split(".");
  const [ firstKey, ...restKeys ] = keys;

  if (restKeys.length === 0) {
    const { [firstKey]: removed, ...rest } = obj as Record<string, unknown>;
    return rest as ICatFormData;
  }

  const nested = removeNestedField(
    (obj as Record<string, unknown>)[firstKey] as Record<string, unknown> ?? {},
    restKeys.join(".") as CatFieldName,
  );

  return { ...obj, [firstKey]: nested } as ICatFormData;
};

/**
 * Updates a nested field within a cat object based on a dot-notated path.
 *
 * @param obj - The original cat object (`ICatFormData`) to update.
 * @param path - The dot-notated (`CatFieldName`) path to the field to update (e.g., "diet.current.brand").
 * @param value - The new value to set at the specified nested path.
 * @returns A new `ICatFormData` object with the updated value, leaving the original object unchanged.
 *
 * @example
 * updateNestedField(cat, "diet.current.brand", "Whiskas");
 * // Returns a new object where cat.diet.current.brand === "Whiskas"
 */
export const updateNestedField = (
  obj: ICatFormData,
  path: CatFieldName,
  value: unknown,
): ICatFormData => {
  const keys = path.split(".");
  const [ firstKey, ...restKeys ] = keys;

  if (restKeys.length === 0) {
    return { ...obj, [firstKey]: value } as ICatFormData;
  }

  const nested = updateNestedField(
    (obj as Record<string, unknown>)[firstKey] as Record<string, unknown> ?? {},
    restKeys.join(".") as CatFieldName,
    value,
  );

  return { ...obj, [firstKey]: nested } as ICatFormData;
};

export const transformRecipesForApi = (cat: ICatFormData): Record<string, number> => {
  const recipeMap: Record<string, number> = {};
  cat.selectedRecipes?.recipes?.forEach((recipe: IRecipeItem) => {
    if (recipe.type) {
      recipeMap[recipe.type] = (recipeMap[recipe.type] || 0) + (recipe.quantity ?? 0);
    }
  });
  return recipeMap;
};

export const getAllNibblesQuantities = (
  upsell: ISubscriptionData["nibblesUpsell"] | undefined,
): { chicken: 0 | 1; salmon: 0 | 1 } => {
  if (!upsell?.added) { return ({ chicken: 0, salmon: 0 }); }

  switch (upsell.type) {
    case "NIBBLES_CHICKEN":
      return { chicken: 1, salmon: 0 };
    case "NIBBLES_SALMON":
      return { chicken: 0, salmon: 1 };
    default:
      return { chicken: 0, salmon: 0 };
  }
};

const nibblesQuantity = (isPrimary: boolean, qty: 0 | 1): 0 | 1 => (isPrimary ? qty : 0);

const buildCatAge = (lifeStage: CatLifeStage, age: ICatFormData["age"]) => ({
  years: lifeStage === CatLifeStage.KITTEN ? undefined : age?.years || undefined,
  months: lifeStage === CatLifeStage.KITTEN ? age?.months || undefined : undefined,
  weeks: lifeStage === CatLifeStage.KITTEN ? age?.weeks || undefined : undefined,
});

export const transformCatDataForApi = (
  cats: Record<string, ICatFormData>,
  subscription: ISubscriptionData,
): ICatRecipeDetails[] => {
  const { chicken, salmon } = getAllNibblesQuantities(subscription.nibblesUpsell);

  const filterCats = Object.values(cats).filter((cat) => !cat.softDelete);
  return filterCats.map((cat, index) => {
    const isPrimary = index === 0;

    const lifeStage = cat.lifeStage ? cat.lifeStage.toUpperCase() as CatLifeStage : CatLifeStage.ADULT;

    return {
      catName: cat.name ?? "",
      sprinklesQuantity: cat.freeGiftTreat?.added ? 1 : 0, // note this is not actually being set in the store and will be removed in SUFv3
      nibblesChickenQuantity: nibblesQuantity(isPrimary, chicken),
      nibblesSalmonQuantity: nibblesQuantity(isPrimary, salmon),
      recipeTitleToQuantity: transformRecipesForApi(cat),
      trialMealPlan: subscription.trial?.planType ?? PricingPlan.TRIAL,
      ongoingMealPlan: subscription.ongoing?.planType ?? PricingPlan.STANDARD,
      weightInKg: cat.weight?.valueInKg ?? undefined,
      bodyType: cat.body?.bodyType ?? undefined,
      lifestyle: cat.body?.activityLevel ?? undefined,
      allergies: cat.diet?.foodAllergies?.allergens ?? [],
      catAge: buildCatAge(lifeStage, cat.age),
    };
  });
};

/**
 * Calculates birthday for kittens based on age in weeks or months.
 * Returns a rough date that will later be adjusted to user's preferred month.
 */
const calculateKittenBirthdayDate = (age: ICatFormData["age"]): dayjs.Dayjs | undefined => {
  if (!age) { return undefined; }

  const ageMode = age.mode ?? "months";

  if (ageMode === "weeks" && age.weeks) {
    return dayjs().add(-age.weeks, "weeks");
  }

  if (ageMode === "months" && age.months) {
    return dayjs().add(-age.months, "months");
  }

  return undefined;
};

/**
 * Calculates birthday for adult/senior cats based on age in years.
 * Returns a rough date that will later be adjusted to user's preferred month.
 */
const calculateAdultBirthdayDate = (age: ICatFormData["age"]): dayjs.Dayjs | undefined => {
  if (!age) { return undefined; }
  if (age.years) { return dayjs().add(-age.years, "years"); }
  return undefined;
};

/**
 * Applies the user's preferred birthday month to a calculated date.
 *
 * Example: If cat is 2 years old (calculated: March 15, 2022) and user
 * prefers JANUARY, result is January 1, 2022.
 *
 * Note: This function is only used for adult/senior cats. Kittens bypass this
 * and have their day standardized directly in calculateCatBirthday.
 *
 * @param date - The calculated birthday date from age calculation
 * @param birthdayMonth - User's preferred month (optional)
 * @returns Date with user's preferred month (or original month if undefined), same year, day set to 1st
 */
const applyBirthdayMonth = (date: dayjs.Dayjs, birthdayMonth?: ICatFormData["birthdayMonth"]): dayjs.Dayjs => {
  if (!birthdayMonth) { return date; }

  const monthMap: Record<string, number> = {
    JANUARY: 0,
    FEBRUARY: 1,
    MARCH: 2,
    APRIL: 3,
    MAY: 4,
    JUNE: 5,
    JULY: 6,
    AUGUST: 7,
    SEPTEMBER: 8,
    OCTOBER: 9,
    NOVEMBER: 10,
    DECEMBER: 11,
  };

  const targetMonth = monthMap[birthdayMonth];
  if (targetMonth === undefined) { return date; }

  let result = date.month(targetMonth).date(1);

  // If applying the birthday month moves the date forward (later in the year),
  // we need to subtract a year to maintain the approximate age.
  if (result.isAfter(date)) {
    result = result.subtract(1, "year");
  }

  return result;
};

/**
 * Calculates the cat's birthday by combining age calculation with user's month preference.
 *
 * This allows us to get the correct YEAR from age calculation while respecting
 * the user's preference for the month.
 *
 * For kittens, the calculated month is preserved (not changed to birthdayMonth) but day is set to 1st.
 *
 * @param cat - The cat form data containing age, life stage, and optional birthday month
 * @returns The calculated birthday as YYYY-MM-DD string, or undefined if calculation fails
 */
export const calculateCatBirthday = (cat: ICatFormData): string | undefined => {
  const { age, lifeStage, birthdayMonth } = cat;

  if (!age || !lifeStage) {
    return undefined;
  }

  let calculatedDate: dayjs.Dayjs | undefined;

  if (lifeStage === "kitten") {
    calculatedDate = calculateKittenBirthdayDate(age);
    // For kittens, just standardize to day 1 but don't apply birthdayMonth
    return calculatedDate ? calculatedDate.date(1).format(DATE_FORMAT) : undefined;
  }
  if (lifeStage === "adult" || lifeStage === "senior") {
    calculatedDate = calculateAdultBirthdayDate(age);
  }

  if (!calculatedDate) {
    return undefined;
  }

  return applyBirthdayMonth(calculatedDate, birthdayMonth).format(DATE_FORMAT);
};

export interface ITransformStoreDataForCreateFreshCustomerParams {
  cats: Record<string, ICatFormData>;
  user: IUserFormData;
  subscription: ISubscriptionData;
  userId?: string;
  formUsed: string;
  trackingInfo?: {
    fbc?: string;
    fbp?: string;
    pageUrl?: string;
    ttclid?: string;
    _ttp?: string;
  };
}

/**
 * Calculates benefits for a cat, including motivation and health concerns if applicable
 *
 * @param cat - Cat form data
 * @returns Array of benefits (motivations and health concerns)
 */
export const calculateCatBenefits = (cat: ICatFormData): {
  benefits: (TMotivationsForSwitch | THealthConcernsToAddress)[];
  primarySwitchReason: TMotivationsForSwitch | null;
  secondarySwitchReasons: THealthConcernsToAddress[];
} => {
  const benefits: (TMotivationsForSwitch | THealthConcernsToAddress)[] = [];
  let primarySwitchReason: TMotivationsForSwitch | null = null;
  const secondarySwitchReasons: THealthConcernsToAddress[] = [];

  if (cat.diet?.motivationsForSwitch) {
    primarySwitchReason = cat.diet.motivationsForSwitch;
    benefits.push(cat.diet.motivationsForSwitch);
  }

  // Add health concerns if the motivation is health-related
  if (cat.diet?.motivationsForSwitch === "SUPPORTS_HEALTH_CONDITIONS" &&
    cat.diet?.healthConcernsToAddress?.length) {
    secondarySwitchReasons.push(...cat.diet.healthConcernsToAddress);
    benefits.push(...cat.diet.healthConcernsToAddress);
  }

  return {
    benefits,
    primarySwitchReason,
    secondarySwitchReasons,
  };
};

/**
 * Transforms store data to the format required for the createFreshCustomer API call
 *
 * @param cats - Record of cat form data from the store
 * @param user - User form data from the store
 * @param subscription - Subscription data from the store
 * @param trialType - Trial type for the customer (defaults to "TWENTY_OFF")
 * @returns ICreateFreshCustomer object ready for the API call
 *
 */
const buildCatDetails = (cat: ICatFormData): IFreshCreateOrUpdateCatData["details"] => {
  const weightInGrams = cat.weight?.valueInKg ? cat.weight.valueInKg * 1000 : DEFAULT_CAT_WEIGHT_GRAMS;

  return {
    breed: cat.breed ?? "UNKNOWN",
    birthday: cat.birthday ?? DEFAULT_CAT_BIRTHDAY,
    isBirthdayPrecise: false,
    bodyType: cat.body?.bodyType ?? "UNKNOWN",
    activityLevel: cat.body?.activityLevel ?? "UNKNOWN",
    isNeutered: cat.isNeutered ?? false,
    weight: weightInGrams,
    allergies: cat.diet?.foodAllergies?.allergens ?? [],
    medicalConditions: cat.medical?.conditions ?? [],
  };
};

const transformCatForAbandonedCart = (cat: ICatFormData): IFreshCreateOrUpdateCatData => ({
  cat: {
    name: cat.name ?? "",
    gender: cat.gender ?? "UNKNOWN",
  },
  details: buildCatDetails(cat),
  answers: {
    ageAtSignup: getAgeAtSignup({
      catLifeStage: cat.lifeStage ? cat.lifeStage.toUpperCase() as CatLifeStage : undefined,
      catAgeYears: cat.age?.years,
      catAgeMonths: cat.age?.months,
      catAgeWeeks: cat.age?.weeks,
      ageMode: cat.age?.mode,
    }),
    foodTypeBeforeKatkin: cat.diet?.current?.type ?? [],
    brandBeforeKatkin: cat.diet?.current?.brand ?? "UNKNOWN",
    fussiness: cat.diet?.eatingHabits ?? "UNKNOWN",
    ...calculateCatBenefits(cat),
  },
  breeder: null,
});

export const transformStoreDataForCreateAbandonedCartCats = ({
  cats,
  user,
  subscription,
  userId,
  formUsed,
  trackingInfo,
}: ITransformStoreDataForCreateFreshCustomerParams): IFreshCreateOrUpdateCatsRequest => {
  const validCats = Object.values(cats).filter((cat) => !cat.softDelete);

  const transformedCats: IFreshCreateOrUpdateCatData[] = validCats.map((cat) => transformCatForAbandonedCart(cat));

  const switchReasons = new Set<TMotivationsForSwitch | THealthConcernsToAddress>();
  const primarySwitchReason = new Set<TMotivationsForSwitch>();
  const secondarySwitchReasons = new Set<THealthConcernsToAddress>();
  validCats.forEach((cat) => {
    const benefits = calculateCatBenefits(cat);
    benefits.benefits.forEach((benefit) => {
      switchReasons.add(benefit);
    });
    if (benefits.primarySwitchReason) {
      primarySwitchReason.add(benefits.primarySwitchReason);
    }
    benefits.secondarySwitchReasons.forEach((reason) => {
      secondarySwitchReasons.add(reason);
    });
  });

  const customerSUFAnswers: IFreshCustomerSUFAnswers = {
    switchReasons: switchReasons.size ? [ ...switchReasons ] : null,
    primarySwitchReason: primarySwitchReason.size ? [ ...primarySwitchReason ] : null,
    secondarySwitchReasons: secondarySwitchReasons.size ? [ ...secondarySwitchReasons ] : null,
  };

  return {
    userId: userId ?? "",
    formUsed,
    consentsToMarketing: user.marketingConsent ?? false,
    cats: transformedCats,
    discountCode: subscription.discount.discountCode ?? undefined,
    customerSUFAnswers,
    fbc: trackingInfo?.fbc,
    fbp: trackingInfo?.fbp,
    pageUrl: trackingInfo?.pageUrl,
    ttclid: trackingInfo?.ttclid,
    ttp: trackingInfo?._ttp,
  };
};
